import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  UsersThreeIcon, CaretRightIcon, CaretDownIcon, CopyIcon, ChartLineUpIcon,
  MagnifyingGlassIcon, QrCodeIcon, UserPlusIcon, EnvelopeSimpleIcon,
} from "@phosphor-icons/react";
import api, { resolveUrl } from "@/lib/api";
import Layout from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const ST = {
  active: "border-primary/40 text-primary",
  pending_review: "border-accent/40 text-accent",
  unclaimed: "border-white/25 text-white/50",
  unallocated: "border-white/20 text-white/40",
};

function CustomerBlock({ c, navigate, onInvite, inviting }) {
  const [open, setOpen] = useState(false);
  const name = [c.first_name, c.last_name].filter(Boolean).join(" ");
  return (
    <div className="border-b border-border last:border-0" data-testid={`cust-${c.email}`}>
      <div onClick={() => setOpen((o) => !o)} data-testid={`cust-toggle-${c.email}`} className="w-full grid grid-cols-12 items-center gap-4 px-6 py-4 hover:bg-secondary/20 transition-colors text-left cursor-pointer">
        <div className="col-span-4 flex items-center gap-3 min-w-0">
          {open ? <CaretDownIcon className="text-primary shrink-0" /> : <CaretRightIcon className="text-white/40 shrink-0" />}
          <div className="min-w-0">
            <p className="font-heading font-bold truncate">{c.email}</p>
            {name && <p className="font-mono text-[11px] text-white/40 truncate">{name}</p>}
          </div>
        </div>
        <div className="col-span-2 text-center"><p className="font-mono text-lg leading-none">{c.code_count}</p><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">codes</p></div>
        <div className="col-span-2 text-center"><p className="font-mono text-lg leading-none text-primary">{c.active}</p><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">live</p></div>
        <div className="col-span-1 text-center"><p className="font-mono text-lg leading-none text-accent">{c.pending}</p><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">pending</p></div>
        <div className="col-span-2 text-right"><p className="font-mono text-lg leading-none">{c.total_scans}</p><p className="font-mono text-[9px] uppercase tracking-widest text-white/40">scans</p></div>
        <div className="col-span-1 flex justify-end">
          <button
            data-testid={`cust-invite-${c.email}`}
            onClick={(e) => { e.stopPropagation(); onInvite(c.email); }}
            disabled={inviting}
            title="Email sign-in link"
            className="flex items-center justify-center h-8 w-8 border border-border text-white/50 hover:text-primary hover:border-primary/40 transition-colors disabled:opacity-40"
          >
            <EnvelopeSimpleIcon weight="bold" />
          </button>
        </div>
      </div>

      {open && (
        <div className="bg-background/60 px-6 pb-5 pt-1">
          {c.codes.map((code) => (
            <div key={code.code} data-testid={`cust-code-${code.code}`} className="grid grid-cols-12 items-center gap-3 border-t border-border/50 py-3">
              <div className="col-span-3 min-w-0">
                <p className="font-mono text-sm text-primary truncate">/{code.code}</p>
                <p className="font-mono text-[10px] text-white/30">{code.confirmed ? "confirmed by customer" : "assigned · awaiting confirm"}</p>
              </div>
              <div className="col-span-2">
                <Badge variant="outline" className={`font-mono text-[10px] uppercase ${ST[code.status] || "border-white/20 text-white/40"}`}>{code.status}</Badge>
              </div>
              <div className="col-span-4 min-w-0">
                <p className="font-mono text-xs text-white/50 truncate">{code.destination_url || "— no destination —"}</p>
              </div>
              <div className="col-span-1 text-right"><span className="font-mono text-sm">{code.scans}</span></div>
              <div className="col-span-2 flex items-center justify-end gap-3">
                <button data-testid={`cust-copy-${code.code}`} onClick={() => { navigator.clipboard.writeText(code.resolve_url); toast.success("Link copied"); }} className="text-white/40 hover:text-primary transition-colors" title="Copy link"><CopyIcon weight="bold" /></button>
                {code.status === "active" && <a href={code.resolve_url} target="_blank" rel="noreferrer" className="text-white/40 hover:text-primary transition-colors" title="Open live link"><QrCodeIcon weight="bold" /></a>}
                <button data-testid={`cust-analytics-${code.code}`} onClick={() => navigate(`/admin/code/${code.code}`)} className="flex items-center gap-1 font-mono text-[11px] text-white/60 hover:text-primary transition-colors" title="View analytics"><ChartLineUpIcon weight="bold" /> STATS</button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminCustomers() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ email: "", first_name: "", last_name: "" });
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/admin/customers"); setData(data); }
    catch { setData({ customers: [], total_customers: 0 }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const invite = async (email) => {
    setInviting(true);
    try {
      const { data } = await api.post("/admin/customers/invite", { email });
      if (data.email_sent) toast.success(`Sign-in link emailed to ${data.email}`);
      else toast.warning(`Code generated for ${data.email}, but email couldn't be sent`, { description: "Check the email service is configured." });
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send invite"); }
    finally { setInviting(false); }
  };

  const addCustomer = async () => {
    if (!form.email.trim() || !form.email.includes("@")) return toast.error("Enter a valid email");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/customers", {
        email: form.email.trim(), first_name: form.first_name.trim() || null, last_name: form.last_name.trim() || null,
      });
      toast.success(data.created ? `Added ${data.email}` : `${data.email} already existed`);
      setAddOpen(false); setForm({ email: "", first_name: "", last_name: "" }); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not add customer"); }
    finally { setSaving(false); }
  };

  const customers = (data?.customers || []).filter((c) =>
    !q.trim() || c.email.includes(q.trim().toLowerCase()) || (c.codes || []).some((x) => x.code.includes(q.trim().toUpperCase()))
  );

  return (
    <Layout>
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-accent mb-3">// Admin · customers</p>
          <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter leading-none">Customers</h1>
          <p className="text-white/50 mt-4 max-w-2xl">Every customer and the D Codes bound to them — assigned or confirmed. Expand a customer to see their codes, destinations and scan analytics.</p>
        </div>
        <Button data-testid="cust-add-btn" onClick={() => setAddOpen(true)} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 font-mono text-xs shrink-0">
          <UserPlusIcon weight="bold" className="mr-1.5" /> ADD CUSTOMER
        </Button>
      </div>

      <div className="relative max-w-md mb-5">
        <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <Input data-testid="cust-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by email or code" className="pl-9 bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
      </div>

      <div className="border border-border" data-testid="cust-table">
        <div className="grid grid-cols-12 gap-4 px-6 py-3 border-b border-border bg-secondary/20 font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">
          <div className="col-span-4">Customer</div>
          <div className="col-span-2 text-center">Codes</div>
          <div className="col-span-2 text-center">Live</div>
          <div className="col-span-1 text-center">Pend</div>
          <div className="col-span-2 text-right">Scans</div>
          <div className="col-span-1 text-right">Invite</div>
        </div>
        {data === null ? (
          <p className="font-mono text-sm text-primary animate-pulse px-6 py-6">LOADING…</p>
        ) : customers.length === 0 ? (
          <div className="flex flex-col items-center text-center py-16 px-6">
            <UsersThreeIcon size={40} weight="duotone" className="text-white/30" />
            <p className="font-heading font-bold text-lg mt-4">No customers yet</p>
            <p className="text-white/40 text-sm mt-1">Customers appear here once codes are assigned to them (via order or the Inventory tab).</p>
          </div>
        ) : (
          customers.map((c) => <CustomerBlock key={c.email} c={c} navigate={navigate} onInvite={invite} inviting={inviting} />)
        )}
      </div>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent data-testid="cust-add-dialog" className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="font-heading">Add a customer</DialogTitle>
            <DialogDescription className="text-white/50">Creates an eligible customer so they can sign in and be assigned codes. If the email already exists, nothing is lost.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Input data-testid="cust-add-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="customer@email.com" className="bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="cust-add-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder="First name (optional)" className="bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
              <Input data-testid="cust-add-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder="Last name (optional)" className="bg-secondary/40 border-border font-mono text-sm focus-visible:ring-primary" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)} className="border-border bg-transparent hover:bg-secondary hover:text-white font-mono text-xs">CANCEL</Button>
            <Button data-testid="cust-add-save" onClick={addCustomer} disabled={saving} className="bg-primary text-primary-foreground font-bold hover:bg-primary/90 font-mono text-xs">{saving ? "ADDING…" : "ADD CUSTOMER"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
